import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudiesPage, prefetchStudyOpenBundle, triggerStudyRenderWarmup } from '../api';
import { useAuth } from '../AuthContext';
import { formatStudyReadiness, KAIROS_WORKLIST_CACHE_READINESS, readinessSortValue } from './studyReadiness';
import { matchesStudyGlobalSearch } from './studySearch';
import {
  acknowledgeStudyWarmupIntent,
  captureStudyWarmupIntentGeneration,
  failStudyWarmupIntent,
  markStudyWarmupIntent,
  reconcileStudyWarmupIntents,
} from './studyWarmupIntent';
import {
  Box, Typography, CircularProgress, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Button,
  TextField, InputAdornment, IconButton, Tooltip, Select, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';


// ─── helpers ────────────────────────────────────────────────────────────────

const formatDateTime = (dateStr, timeStr = '') => {
  if (!dateStr || dateStr.length < 8) return '—';
  const d = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  if (timeStr && timeStr.length >= 6) {
    const t = `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:${timeStr.slice(4, 6)}`;
    return `${d} ${t}`;
  }
  return d;
};

const MODALITY_COLORS = {
  CT: { bg: '#003366', fg: '#00bcd4' },
  MR: { bg: '#1a0033', fg: '#ce93d8' },
  CR: { bg: '#002200', fg: '#66bb6a' },
  DX: { bg: '#002200', fg: '#66bb6a' },
  US: { bg: '#001a33', fg: '#4fc3f7' },
  NM: { bg: '#330000', fg: '#ef9a9a' },
  PT: { bg: '#331a00', fg: '#ffb74d' },
  XA: { bg: '#003333', fg: '#80cbc4' },
};
const modalityColor = m => MODALITY_COLORS[m] || { bg: '#1a1a1a', fg: '#aaa' };

// ─── column definitions ─────────────────────────────────────────────────────

export const KAIROS_WORKLIST_STICKY_HEADER = 'KAIROS_WORKLIST_STICKY_HEADER';
export const KAIROS_WORKLIST_MODERN_UI_V1 = 'KAIROS_WORKLIST_MODERN_UI_V1';
const DEFAULT_STUDY_PAGE_SIZE = 50;
const STUDY_PAGE_SIZE_OPTIONS = [25, 50, 100];

const COLUMNS = [
  { id: 'patientId',        label: 'ID',          searchable: true,  sortable: true,  width: 110 },
  { id: 'patientName',      label: 'Name',        searchable: true,  sortable: true,  width: 180 },
  { id: 'accessionNumber',  label: 'Accession',   searchable: true,  sortable: true,  width: 130 },
  { id: 'modalities',       label: 'Modality',    searchable: true,  sortable: true,  width: 110 },
  { id: 'studyDescription', label: 'Description', searchable: true,  sortable: true,  width: 200 },
  { id: 'dateTime',         label: 'Date Time',   searchable: false, sortable: true,  width: 150 },
  { id: 'receivedOn',       label: 'Received On', searchable: false, sortable: true,  width: 150 },
  { id: 'readinessState',   label: 'Ready',       searchable: false, sortable: true,  width: 105 },
  { id: 'sourceAE',         label: 'Source AE',   searchable: true,  sortable: true,  width: 110 },
  { id: 'numSeries',        label: 'Series',      searchable: false, sortable: true,  width: 70  },
  { id: 'numInstances',     label: 'Instances',   searchable: false, sortable: true,  width: 80  },
];

// ─── SortIcon ───────────────────────────────────────────────────────────────

const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return <UnfoldMoreIcon sx={{ fontSize: 13, color: '#555', ml: 0.3 }} />;
  return sortDir === 'asc'
    ? <ArrowUpwardIcon sx={{ fontSize: 13, color: 'primary.main', ml: 0.3 }} />
    : <ArrowDownwardIcon sx={{ fontSize: 13, color: 'primary.main', ml: 0.3 }} />;
};

// ─── ColumnHeader ────────────────────────────────────────────────────────────

const ColumnHeader = ({ col, sortCol, sortDir, colSearch, onSort, onColSearch }) => {
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <TableCell
      sx={{
        bgcolor: '#081117',
        background: 'linear-gradient(180deg, rgba(12,27,37,0.98), rgba(5,11,16,0.98))',
        color: '#6eefff',
        fontWeight: 'bold',
        borderBottom: '1px solid rgba(110,239,255,0.18)',
        borderRight: '1px solid rgba(110,239,255,0.08)',
        fontSize: 11,
        letterSpacing: 0.8,
        p: 0,
        width: col.width,
        minWidth: col.width,
        userSelect: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* label + icons row */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.8, cursor: col.sortable ? 'pointer' : 'default' }}
          onClick={() => col.sortable && onSort(col.id)}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: 'primary.main', flex: 1 }}>
            {col.label.toUpperCase()}
          </Typography>
          {col.searchable && (
            <Tooltip title="Filter column">
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); setSearchOpen(v => !v); }}
                sx={{ p: 0.2, color: colSearch ? 'primary.main' : '#555', '&:hover': { color: 'primary.main' } }}
              >
                <SearchIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          )}
          {col.sortable && <SortIcon col={col.id} sortCol={sortCol} sortDir={sortDir} />}
        </Box>
        {/* per-column search input */}
        {col.searchable && searchOpen && (
          <Box sx={{ px: 1, pb: 0.5 }}>
            <TextField
              autoFocus
              size="small"
              value={colSearch || ''}
              onChange={e => onColSearch(col.id, e.target.value)}
              onClick={e => e.stopPropagation()}
              placeholder={`Filter…`}
              inputProps={{ style: { fontSize: 11, padding: '2px 6px', color: '#ccc' } }}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.25,
                  bgcolor: 'rgba(2,8,12,0.86)',
                  '& fieldset': { borderColor: 'rgba(110,239,255,0.14)' },
                  '&:hover fieldset': { borderColor: 'rgba(110,239,255,0.30)' },
                  '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
                },
              }}
            />
          </Box>
        )}
      </Box>
    </TableCell>
  );
};

// ─── StudyList ───────────────────────────────────────────────────────────────

const StudyList = () => {
  const [studies, setStudies]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [globalSearch, setGlobal]   = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageError, setPageError]   = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_STUDY_PAGE_SIZE);
  const [dateOrder, setDateOrder] = useState('study_date_desc');
  const [pageIndex, setPageIndex] = useState(0);
  const [colSearch, setColSearch]   = useState({});   // { colId: filterString }
  const [sortCol, setSortCol]       = useState('dateTime');
  const [sortDir, setSortDir]       = useState('desc');
  const requestGenerationRef = useRef(0);
  const hasLoadedAdditionalPagesRef = useRef(false);
  const loadMoreInFlightRef = useRef(false);
  const pageCursorsRef = useRef([null]);
  const navigate = useNavigate();
  const goAdmin = () => navigate('/admin');
  const goUserSettings = () => navigate('/settings');
  const { user, logout } = useAuth();
  const canAdminister = (user?.roles || []).some(role => ['admin', 'superadmin'].includes(String(role).toLowerCase()));
  const handleLogout = async () => { await logout(); navigate('/login', { replace: true }); };

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(globalSearch.trim()), 250);
    return () => clearTimeout(id);
  }, [globalSearch]);

  const loadStudies = useCallback((showLoading = false, cursorValue = null, showPageLoading = false) => {
    const intentGenerationAtRequest = captureStudyWarmupIntentGeneration();
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    if (showLoading) setLoading(true);
    if (showPageLoading) setLoadingMore(true);
    if (showLoading) setError(null);
    setPageError(null);
    return getStudiesPage({
      q: debouncedSearch || undefined,
      limit: pageSize,
      sort: dateOrder,
      cursor: cursorValue || undefined,
    })
      .then(page => {
        if (generation !== requestGenerationRef.current) return false;
        setStudies(reconcileStudyWarmupIntents(page.studies, intentGenerationAtRequest));
        setNextCursor(page.nextCursor);
        setHasMore(page.hasMore);
        return true;
      })
      .catch(e => {
        if (generation !== requestGenerationRef.current) return false;
        if (showLoading) setError(e.message);
        else setPageError(e.message);
        return false;
      })
      .finally(() => {
        if (generation !== requestGenerationRef.current) return;
        if (showLoading) setLoading(false);
        if (showPageLoading) setLoadingMore(false);
      });
  }, [debouncedSearch, pageSize, dateOrder]);

  useEffect(() => {
    pageCursorsRef.current = [null];
    setPageIndex(0);
    hasLoadedAdditionalPagesRef.current = false;
    loadStudies(true, null);
    const id = setInterval(() => {
      if (!document.hidden && !hasLoadedAdditionalPagesRef.current) loadStudies(false, null);
    }, 10000);
    return () => clearInterval(id);
  }, [loadStudies]);

  const loadPage = useCallback((targetIndex, cursorValue) => {
    if (loadingMore || loadMoreInFlightRef.current) return;
    loadMoreInFlightRef.current = true;
    // Suspend first-page polling for the entire explicit navigation so it cannot
    // supersede this cursor request in the shared generation fence.
    hasLoadedAdditionalPagesRef.current = true;
    loadStudies(false, cursorValue, true)
      .then(loaded => {
        if (!loaded) {
          hasLoadedAdditionalPagesRef.current = pageIndex > 0;
          return;
        }
        setPageIndex(targetIndex);
        hasLoadedAdditionalPagesRef.current = targetIndex > 0;
      })
      .finally(() => { loadMoreInFlightRef.current = false; });
  }, [loadingMore, loadStudies, pageIndex]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || !nextCursor) return;
    const targetIndex = pageIndex + 1;
    pageCursorsRef.current = [...pageCursorsRef.current.slice(0, targetIndex), nextCursor];
    loadPage(targetIndex, nextCursor);
  }, [hasMore, nextCursor, pageIndex, loadPage]);

  const loadPreviousPage = useCallback(() => {
    if (pageIndex < 1) return;
    const targetIndex = pageIndex - 1;
    loadPage(targetIndex, pageCursorsRef.current[targetIndex] || null);
  }, [pageIndex, loadPage]);

  const handleOpenStudy = useCallback((study) => {
    if (!study?.studyInstanceUID) return;
    markStudyWarmupIntent(study.studyInstanceUID);
    setStudies(prev => prev.map(item => item.studyInstanceUID === study.studyInstanceUID
      ? { ...item, renderCacheStatus: 'warming', warmupPending: Math.max(1, Number(item.warmupPending || 0)), warmupTotal: Math.max(1, Number(item.warmupTotal || 0)) }
      : item));
    prefetchStudyOpenBundle(study.studyInstanceUID).catch(e => {
      console.warn('prefetchStudyOpenBundle failed; viewer will retry:', e);
    });
    triggerStudyRenderWarmup(study.studyInstanceUID)
      .then(() => acknowledgeStudyWarmupIntent(study.studyInstanceUID))
      .catch(e => {
        failStudyWarmupIntent(study.studyInstanceUID);
        console.warn('triggerStudyRenderWarmup failed:', e);
        setStudies(prev => prev.map(item => item.studyInstanceUID === study.studyInstanceUID
          ? { ...item, renderCacheStatus: null, warmupPending: 0, warmupTotal: 0 }
          : item));
      });
    navigate(`/study/${study.studyInstanceUID}`);
  }, [navigate]);

  const applyDateOrder = useCallback(nextOrder => {
    if (!['study_date_desc', 'study_date_asc'].includes(nextOrder)) return;
    setDateOrder(nextOrder);
    setSortCol('dateTime');
    setSortDir(nextOrder === 'study_date_asc' ? 'asc' : 'desc');
  }, []);

  const handleSort = useCallback(colId => {
    if (colId === 'dateTime') {
      applyDateOrder(dateOrder === 'study_date_desc' ? 'study_date_asc' : 'study_date_desc');
      return;
    }
    setSortCol(prev => {
      if (prev === colId) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return colId; }
      setSortDir('asc');
      return colId;
    });
  }, [applyDateOrder, dateOrder]);

  const handleColSearch = useCallback((colId, val) => {
    setColSearch(prev => ({ ...prev, [colId]: val }));
  }, []);

  // Flatten dateTime for sorting/display
  const enriched = useMemo(() => studies.map(s => {
    const readiness = formatStudyReadiness(s);
    return {
      ...s,
      dateTime: s.studyDate
        ? `${s.studyDate}${s.studyTime || ''}`.replace(/\D/g, '')
        : '',
      dateTimeDisplay: formatDateTime(s.studyDate, s.studyTime),
      // receivedOn already formatted as "YYYY-MM-DD HH:MM:SS" from api.js
      receivedOnDisplay: s.receivedOn || '—',
      readiness,
      readinessState: readiness.state,
    };
  }), [studies]);

  // Global filter
  const globalFiltered = enriched.filter(s => matchesStudyGlobalSearch(s, globalSearch));

  // Per-column filters
  const colFiltered = globalFiltered.filter(s =>
    Object.entries(colSearch).every(([col, val]) => {
      if (!val) return true;
      const field = col === 'modalities' ? s.modalities : s[col];
      return String(field || '').toLowerCase().includes(val.toLowerCase());
    })
  );

  // Sort
  const sorted = [...colFiltered].sort((a, b) => {
    let av = sortCol === 'readinessState' ? readinessSortValue(a.readiness) : (a[sortCol] ?? '');
    let bv = sortCol === 'readinessState' ? readinessSortValue(b.readiness) : (b[sortCol] ?? '');
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box
      aria-label="Diagnostic worklist"
      data-kairos-marker={KAIROS_WORKLIST_MODERN_UI_V1}
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: { xs: 1, md: 1.5 },
        overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(10,18,26,0.98), rgba(4,7,11,1))',
        color: '#d7fbff',
      }}
    >

      {/* ── Worklist command deck ── */}
      <Box sx={{
        px: { xs: 1.25, md: 2 }, py: 1.1,
        border: '1px solid rgba(110,239,255,0.14)',
        borderRadius: 2,
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25,
        bgcolor: 'rgba(5,11,16,0.86)',
        background: 'linear-gradient(135deg, rgba(8,20,29,0.94), rgba(4,8,12,0.88))',
        backdropFilter: 'blur(14px)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1.25, display: 'grid', placeItems: 'center', color: '#6eefff', border: '1px solid rgba(110,239,255,0.28)', bgcolor: 'rgba(0,188,212,0.08)', fontSize: 14 }}>
            ◈
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#d7fbff', fontWeight: 800, letterSpacing: 1.8, fontSize: 14, lineHeight: 1.05 }}>
              KAIROS
            </Typography>
            <Typography sx={{ color: 'rgba(110,239,255,0.62)', fontWeight: 800, letterSpacing: 1.35, fontSize: 8, lineHeight: 1.2 }}>
              DIAGNOSTIC WORKLIST
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(215,251,255,0.42)', fontFamily: 'monospace', fontSize: 9 }}>
          {sorted.length} filtered · {studies.length} loaded · page {pageIndex + 1}
        </Typography>
        <Box data-kairos-marker={KAIROS_WORKLIST_CACHE_READINESS} sx={{ display: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: 9, color: '#55717a', fontFamily: 'monospace', letterSpacing: 0.8 }}>
            PAGE SIZE
          </Typography>
          <Select
            aria-label="Studies per page"
            size="small"
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            sx={{
              minWidth: 68, height: 28, borderRadius: 1.25,
              color: '#d7fbff', fontSize: 11, bgcolor: 'rgba(0,188,212,0.06)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(110,239,255,0.18)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(110,239,255,0.36)' },
              '& .MuiSelect-icon': { color: '#6eefff' },
            }}
          >
            {STUDY_PAGE_SIZE_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </Box>
        <Select
          aria-label="Study date order"
          size="small"
          value={dateOrder}
          onChange={e => applyDateOrder(e.target.value)}
          sx={{
            minWidth: 132, height: 28, borderRadius: 1.25,
            color: '#d7fbff', fontSize: 11, bgcolor: 'rgba(0,188,212,0.06)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(110,239,255,0.18)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(110,239,255,0.36)' },
            '& .MuiSelect-icon': { color: '#6eefff' },
          }}
        >
          <MenuItem value="study_date_desc">Newest study first</MenuItem>
          <MenuItem value="study_date_asc">Oldest study first</MenuItem>
        </Select>
        <Box sx={{ flex: 1 }} />
        {user && (
          <Typography sx={{ fontSize: 10, color: 'rgba(215,251,255,0.42)', fontFamily: 'monospace', mr: 0.5 }}>
            {user.username}
          </Typography>
        )}
        <Tooltip title="My viewer profile settings">
          <IconButton onClick={goUserSettings} size="small" aria-label="Open my viewer profile settings" sx={{ color: '#78909c', borderRadius: 1.25, bgcolor: 'rgba(110,239,255,0.04)', '&:hover': { color: '#6eefff', bgcolor: 'rgba(0,188,212,0.10)' } }}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={canAdminister ? 'Admin' : 'Monitoring'}>
          <IconButton onClick={goAdmin} size="small" aria-label={canAdminister ? 'Open admin console' : 'Open monitoring status'} sx={{ color: '#78909c', borderRadius: 1.25, bgcolor: 'rgba(110,239,255,0.04)', '&:hover': { color: '#6eefff', bgcolor: 'rgba(0,188,212,0.10)' } }}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Sign out">
          <IconButton onClick={handleLogout} size="small" sx={{ color: '#667788', borderRadius: 1.25, '&:hover': { color: '#ff8a8a', bgcolor: 'rgba(255,107,107,0.08)' } }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <TextField
          size="small"
          placeholder="Search all fields…"
          value={globalSearch}
          onChange={e => setGlobal(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: '#555' }} /></InputAdornment>,
          }}
          sx={{
            width: { xs: '100%', sm: 260, lg: 320 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              bgcolor: 'rgba(2,8,12,0.78)',
              '& fieldset': { borderColor: 'rgba(110,239,255,0.16)' },
              '&:hover fieldset': { borderColor: 'rgba(110,239,255,0.34)' },
              '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
            },
            '& input': { fontSize: 12, color: '#d7fbff' },
          }}
        />
      </Box>

      {/* ── Table ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }} data-kairos-marker={KAIROS_WORKLIST_STICKY_HEADER}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {error && (
          <Typography color="error" sx={{ mt: 4, textAlign: 'center', fontSize: 13 }}>
            Error: {error}
          </Typography>
        )}
        {!loading && !error && (
          <TableContainer
            component={Paper}
            sx={{
              bgcolor: '#071017',
              background: 'linear-gradient(180deg, rgba(7,16,23,0.98), rgba(3,8,12,0.99))',
              borderRadius: 2,
              border: '1px solid rgba(110,239,255,0.14)',
              boxShadow: '0 18px 48px rgba(0,0,0,0.34)',
              height: '100%',
              maxHeight: '100%',
              overflow: 'auto',
              overflowX: 'auto',
              overflowY: 'auto',
            }}
          >
            {/* minWidth tracks the sum of fixed column widths so the readiness column scrolls instead of compressing clinical worklist text. */}
            <Table
              size="small"
              stickyHeader
              sx={{
                tableLayout: 'fixed',
                minWidth: COLUMNS.reduce((sum, column) => sum + column.width, 0),
                '& .MuiTableCell-body': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            >

              {/* ── Headers ── */}
              <TableHead>
                <TableRow>
                  {COLUMNS.map(col => (
                    <ColumnHeader
                      key={col.id}
                      col={col}
                      sortCol={sortCol}
                      sortDir={sortDir}
                      colSearch={colSearch[col.id] || ''}
                      onSort={handleSort}
                      onColSearch={handleColSearch}
                    />
                  ))}
                </TableRow>
              </TableHead>

              {/* ── Rows ── */}
              <TableBody>
                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} sx={{ textAlign: 'center', color: 'text.secondary', py: 8, fontSize: 13 }}>
                      No studies found
                    </TableCell>
                  </TableRow>
                )}
                {sorted.map((s, i) => (
                  <TableRow
                    key={s.studyInstanceUID}
                    hover
                    onClick={() => handleOpenStudy(s)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: i % 2 === 0 ? 'rgba(7,15,21,0.94)' : 'rgba(9,19,26,0.94)',
                      transition: 'background-color 120ms ease, box-shadow 120ms ease',
                      '& td': { borderBottomColor: 'rgba(110,239,255,0.055)' },
                      '&:hover': {
                        bgcolor: 'rgba(13,42,53,0.88)',
                        boxShadow: 'inset 3px 0 0 rgba(110,239,255,0.72)',
                      },
                    }}
                  >
                    {/* ID */}
                    <TableCell sx={{ color: '#aaa', fontSize: 12, fontFamily: 'monospace', borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.patientId || '—'}
                    </TableCell>

                    {/* Name */}
                    <TableCell sx={{ color: '#e0e0e0', fontSize: 12, fontWeight: 500, borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.patientName}
                    </TableCell>

                    {/* Accession */}
                    <TableCell sx={{ color: '#aaa', fontSize: 12, fontFamily: 'monospace', borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.accessionNumber || '—'}
                    </TableCell>

                    {/* Modality */}
                    <TableCell sx={{ borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.modalities.split('/').filter(Boolean).map(m => {
                        const c = modalityColor(m);
                        return (
                          <Chip
                            key={m}
                            label={m}
                            size="small"
                            sx={{ fontSize: 10, height: 18, mr: 0.4, bgcolor: c.bg, color: c.fg, borderRadius: 1, fontWeight: 700, border: `1px solid ${c.fg}30` }}
                          />
                        );
                      })}
                    </TableCell>

                    {/* Description */}
                    <TableCell sx={{ color: '#aaa', fontSize: 12, borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.studyDescription || '—'}
                      </Typography>
                    </TableCell>

                    {/* Date Time */}
                    <TableCell sx={{ color: '#aaa', fontSize: 11, fontFamily: 'monospace', borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.dateTimeDisplay}
                    </TableCell>

                    {/* Received On */}
                    <TableCell sx={{ color: '#aaa', fontSize: 11, fontFamily: 'monospace', borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.receivedOnDisplay}
                    </TableCell>

                    {/* Readiness */}
                    <TableCell sx={{ borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      <Tooltip title={<span style={{ whiteSpace: 'nowrap' }}>{s.readiness.detail}</span>}>
                        <Chip
                          label={s.readiness.label}
                          size="small"
                          aria-label={`Render cache readiness: ${s.readiness.label.toLowerCase()} — ${s.readiness.detail}`}
                          sx={{
                            fontSize: 9,
                            height: 18,
                            borderRadius: 1,
                            fontWeight: 800,
                            letterSpacing: 0.6,
                            bgcolor: s.readiness.state === 'HOT' ? '#06351f' : s.readiness.state === 'WARMING' ? '#332700' : '#151515',
                            color: s.readiness.state === 'HOT' ? '#63e6a6' : s.readiness.state === 'WARMING' ? '#ffd166' : '#aaa',
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    {/* Source AE */}
                    <TableCell sx={{ color: '#666', fontSize: 11, fontFamily: 'monospace', borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.sourceAE || '—'}
                    </TableCell>

                    {/* Series */}
                    <TableCell sx={{ color: '#aaa', fontSize: 12, textAlign: 'center', fontFamily: 'monospace', borderRight: '1px solid #1a1a1a', py: 0.8, px: 1.5 }}>
                      {s.numSeries}
                    </TableCell>

                    {/* Instances */}
                    <TableCell sx={{ color: '#aaa', fontSize: 12, textAlign: 'center', fontFamily: 'monospace', py: 0.8, px: 1.5 }}>
                      {s.numInstances}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {(pageIndex > 0 || hasMore || pageError) && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 2, borderTop: '1px solid #222' }}>
                {pageError && (
                  <Typography color="error" sx={{ fontSize: 11 }}>
                    This study page could not be loaded.
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  disabled={loadingMore || pageIndex === 0}
                  onClick={loadPreviousPage}
                  sx={{ borderRadius: 1.25, fontSize: 11 }}
                >
                  Previous page
                </Button>
                <Typography sx={{ color: '#78909c', fontFamily: 'monospace', fontSize: 10 }}>
                  Page {pageIndex + 1} · {studies.length} / {pageSize}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={loadingMore || !nextCursor}
                  onClick={loadNextPage}
                  sx={{ borderRadius: 1.25, fontSize: 11 }}
                >
                  {pageError ? 'Retry loading studies' : loadingMore ? 'Loading studies…' : 'Next page'}
                </Button>
              </Box>
            )}
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default StudyList;
